import io from "socket.io-client";
import { setRoomId, setParticipants, setSocketId, setScreenSharingUser } from "../store/actions";
import store from "../store/store";
import * as webRTCHandler from "./webRTCHandler";
import { appendNewMessageToChatHistory } from "./directMessages";

// const SERVER = "http://localhost:5002";
// const SERVER = "https://imed.serveis.in/";
const getServerUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const protocol = isLocalhost ? 'http' : 'https';
  // Use same domain as frontend when in production (serves both Socket.io and static files)
  return isLocalhost ? "http://localhost:5002" : `${protocol}://${hostname}`;
};

let socket = null;

// Helper function to get photoURL from Redux state
const getPhotoURL = () => {
  const state = store.getState();
  return state.user?.photoURL || null;
};

export const connectWithSocketIOServer = () => {
  // console.log("Connecting to Socket.IO server at:", SERVER);
  const serverUrl = getServerUrl();
  socket = io(serverUrl);

  socket.on("connect", () => {
    // console.log("Connected to Socket.IO server. Socket ID:", socket.id);
    store.dispatch(setSocketId(socket.id));
  });

  socket.on("room-id", (data) => {
    const { roomId } = data;
    // console.log("Received room ID from server:", roomId);
    store.dispatch(setRoomId(roomId));
  });

  socket.on("room-update", (data) => {
    // console.log("Received room update:", data);
    if (data?.connectedUsers?.length > 0) {
      // console.log("Connected users:", data.connectedUsers);
      store.dispatch(setParticipants(data.connectedUsers));
    } else {
      console.error("Invalid or empty room-update data:", data);
    }
  });

  socket.on("conn-prepare", (data) => {
    // console.log("Preparing for new connection. Data received:", data);
    const { connUserSocketId } = data;

    webRTCHandler.prepareNewPeerConnection(connUserSocketId, false);

    // Inform the user who just joined that we have prepared for the connection
    // console.log("Emitting conn-init to:", connUserSocketId);
    socket.emit("conn-init", { connUserSocketId });
  });

  socket.on("conn-signal", (data) => {
    // console.log("Received signaling data:", data);
    webRTCHandler.handleSignalingData(data);
  });

  socket.on("conn-init", (data) => {
    // console.log("Initializing connection with existing user. Data received:", data);
    const { connUserSocketId } = data;
    webRTCHandler.prepareNewPeerConnection(connUserSocketId, true);
  });

  socket.on("user-disconnected", (data) => {
    console.log("User disconnected. Data received:", data);
    webRTCHandler.removePeerConnection(data);
  });

  socket.on("direct-message", (data) => {
    // console.log("Received direct message:", data);
    appendNewMessageToChatHistory(data);
  });

  // Handle participant muted by host
  socket.on("participant-muted", (data) => {
    console.log("You have been muted by the host", data);
    webRTCHandler.forceMute(true);
    // Show notification to user
    if (data.muteAll) {
      alert("Host has muted all participants");
    } else {
      alert("You have been muted by the host");
    }
  });

  // Handle participant removed by host
  socket.on("participant-removed", (data) => {
    console.log("You have been removed from the room", data);
    webRTCHandler.cleanupAllConnections();
    alert("You have been removed from the meeting by the host");
    window.location.href = window.location.origin;
  });

  // Handle screen share started by another user
  socket.on("screen-share-started", (data) => {
    console.log("Screen share started by:", data);
    store.dispatch(setScreenSharingUser(data.identity));
  });

  // Handle screen share stopped
  socket.on("screen-share-stopped", (data) => {
    console.log("Screen share stopped by:", data);
    store.dispatch(setScreenSharingUser(null));
  });

  // Handle screen share error
  socket.on("screen-share-error", (data) => {
    console.log("Screen share error:", data);
    alert(data.error);
  });

  // Handle meeting ended by host
  socket.on("meeting-ended", (data) => {
    console.log("Meeting ended by host:", data);
    webRTCHandler.cleanupAllConnections();
    alert("The host has ended the meeting");
    window.location.href = window.location.origin;
  });
};

export const leaveRoom = () => {
  webRTCHandler.cleanupAllConnections();
  socket.emit("leave-room");
};

export const createNewRoom = (identity, onlyAudio, roomId, callback) => {
  // console.log("Creating a new room. Identity:", identity, "Only audio:", onlyAudio);
  const photoURL = getPhotoURL();
  const data = { identity, onlyAudio, roomId, photoURL };

  socket.emit("create-new-room", data, (roomId) => {
    console.log("Room created with ID:", roomId);
    if (callback) {
      callback(roomId);
    }
  });
};

export const joinRoom = (identity, roomId, onlyAudio) => {
  // console.log("Joining room. Identity:", identity, "Room ID:", roomId, "Only audio:", onlyAudio);
  const photoURL = getPhotoURL();
  const data = { roomId, identity, onlyAudio, photoURL };

  socket.emit("join-room", data);
};

export const signalPeerData = (data) => {
  // console.log("Sending peer signaling data:", data);
  socket.emit("conn-signal", data);
};

export const sendDirectMessage = (data) => {
  // console.log("Sending direct message:", data);
  socket.emit("direct-message", data);
};

// Admin controls - Mute a specific participant
export const muteParticipant = (participantSocketId, roomId) => {
  socket.emit("mute-participant", { participantSocketId, roomId });
};

// Admin controls - Remove a participant from the room
export const removeParticipant = (participantSocketId, roomId) => {
  socket.emit("remove-participant", { participantSocketId, roomId });
};

// Admin controls - Mute all participants (except host)
export const muteAllParticipants = (roomId) => {
  socket.emit("mute-all-participants", { roomId });
};

// Screen sharing - Start screen sharing
export const startScreenShare = (roomId, identity) => {
  socket.emit("screen-share-start", { roomId, identity });
};

// Screen sharing - Stop screen sharing
export const stopScreenShare = (roomId) => {
  socket.emit("screen-share-stop", { roomId });
};

// Host - Signal intentional leaving to end meeting for all
export const hostLeaveMeeting = (roomId, isHost) => {
  if (isHost) {
    socket.emit("host-leave-room", { roomId });
  }
};
